import { RoundedFilledButton } from '@/components/buttons/Buttons';
import Container from '@/components/container/Container';
import Spacing from '@/components/container/Spacing';
import { TextBodySmall } from '@/components/text/Texts';
import { RESEND_OTP, SUBMIT } from '@/constants/button-labels';
import routes from '@/constants/routes';
import { useNavigate, useLocation } from 'react-router-dom';
import { InputOtp } from 'primereact/inputotp';
import { useEffect, useState } from 'react';
import { checkOtp, requestToken, resendOtp } from '@/modules/authentication/engine/authentication.mutations';

import '../../authentication/authentication-screens.styles.css';
import { useAuthenticationStore } from '@/store';
import { cn } from '@/components/ui/cn';
import { Button } from 'primereact/button';
import { useMutation } from '@tanstack/react-query';
import LoadingDialog from '@/components/utils/LoadingDialog';

const OTPForm = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { setAccessToken, setUser } = useAuthenticationStore();

	// 180 is server timeout, don't try shorten it on client side, you will get server error.
	const [secondsLeft, setSecondsLeft] = useState(180);
	const [isTimerActive, setIsTimerActive] = useState(true);
	const [otpValue, setOtpValue] = useState<string>('');
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('Incorrect OTP. Please try again.');

	// ProcessId is seeded from router state and refreshed by each successful resend,
	// since the backend issues a new ProcessId and deletes the previous one on resend.
	const [processId, setProcessId] = useState<string | undefined>(location.state?.processId);

	const isCountingDown = secondsLeft > 0;

	useEffect(() => {
		let interval: NodeJS.Timeout | null = null;

		if (isTimerActive && isCountingDown) {
			interval = setInterval(() => {
				setSecondsLeft((prevSeconds) => {
					if (prevSeconds <= 1) {
						setIsTimerActive(false);
						return 0;
					}
					return prevSeconds - 1;
				});
			}, 1000);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [isTimerActive, secondsLeft]);

	// Auto-submit when all 6 digits are entered
	useEffect(() => {
		if (otpValue.length === 6) {
			mutate();
		}
	}, [otpValue]);

	const { mutate, isPending, isSuccess } = useMutation({
		mutationFn: async () => {
			if (!processId) {
				setErrorMessage('Process ID not found. Please try logging in again.');
				setIsError(true);
				return;
			}
			if (otpValue.length !== 6) return;

			const otpRes = await checkOtp({
				ProcessId: processId,
				Password: otpValue,
			});

			if (!otpRes.data.Success) {
				setErrorMessage(otpRes.data.ErrorMessage || 'OTP Error');
				setIsError(true);
				setOtpValue('');
			}

			const response = await requestToken(processId);

			setAccessToken(response.access_token);
			setUser({
				email: response.Email,
				firstName: response.firstName,
				lastName: response.lastName,
				roleId: response.roleId,
			});

			return response;
		},
		onSuccess: () => {
			navigate(routes['/']);
		},
		onError: (error: any) => {
			setErrorMessage(error.message ?? 'An error occurred. Please try again.');
			setIsError(true);
			setOtpValue('');
		},
	});

	const isLoading = isPending || isSuccess;

	const handleResendOTP = async () => {
		if (secondsLeft !== 0 || !processId) return;

		setIsError(false);
		setSecondsLeft(180);
		setIsTimerActive(true);

		try {
			const response = await resendOtp({
				ProcessId: processId,
				BroadcastChannelType: 3, // Email
			});
			if (response.data.Success) {
				setProcessId(response.data.ProcessId);
			} else {
				setErrorMessage(response.data.ErrorMessage || 'Failed to resend OTP.');
				setIsError(true);
				setSecondsLeft(0);
				setIsTimerActive(false);
			}
		} catch {
			setErrorMessage('Failed to resend OTP. Please try again.');
			setIsError(true);
			setSecondsLeft(0);
			setIsTimerActive(false);
		}
	};

	const handleOtpChange = (event: { value?: string | number | null }) => {
		const value = event.value?.toString() || '';
		setOtpValue(value);
		setIsError(false); // Clear error when user starts typing
	};

	return (
		<>
			<LoadingDialog isLoading={isLoading} />

			<Container
				className={`otp-input-container ${isError ? 'error' : ''}`}
			>
				<InputOtp
					length={6}
					integerOnly
					variant="outlined"
					value={otpValue}
					onChange={handleOtpChange}
					mask={false}
				/>
			</Container>
			{isError && (
				<TextBodySmall
					className="mt-4 p-error block p-inputotp text-center"
					text={errorMessage}
				/>
			)}
			<Button
				id="resend-otp"
				pt={{
					root: { className: cn('mt-8 w-full h-12 rounded-[100px]', isTimerActive ? 'bg-primary99' : 'bg-primary60') },
					label: { className: cn('text-label-large2', isTimerActive ? 'text-secondary60' : 'text-white') },
				}}
				label={isCountingDown ? `${RESEND_OTP} (${secondsLeft})` : RESEND_OTP}
				onClick={handleResendOTP}
				disabled={isCountingDown}
			/>
		</>
	);
};

export default OTPForm;

